import Phaser from 'phaser';
import { NoopHowl, type HowlLike } from '@/systems/AudioBus';
import type { MusicKey } from '@/data/musicManifest';
import type { MusicFactory } from '@/systems/MusicDirectorAdapter';

/**
 * HowlLike backed by a real Phaser sound instance (WebAudio or HTML5).
 *
 * The MusicDirectorAdapter speaks the slim HowlLike API (play/stop/volume/loop);
 * this adapts it onto Phaser's sound manager so the shipped .ogg OST actually
 * plays, replacing the silent NoopHowl default.
 */
class PhaserHowl implements HowlLike {
  private readonly sound: Phaser.Sound.WebAudioSound;

  constructor(scene: Phaser.Scene, key: string) {
    // add() returns BaseSound; the game runs WebAudio by default and both
    // backends implement setVolume/setLoop, so the cast is safe at runtime.
    this.sound = scene.sound.add(key) as Phaser.Sound.WebAudioSound;
  }

  play(): void {
    if (!this.sound.isPlaying) this.sound.play();
  }

  stop(): void {
    this.sound.stop();
  }

  volume(v: number): void {
    this.sound.setVolume(v);
  }

  loop(l: boolean): void {
    this.sound.setLoop(l);
  }
}

/**
 * Build a MusicFactory that plays the loaded .ogg tracks through Phaser.
 *
 * Any key whose asset is missing from the audio cache resolves to a silent
 * NoopHowl, so an absent track degrades gracefully instead of throwing.
 * (Browser autoplay policy is handled by Phaser's sound manager, which unlocks
 *  on the player's first key/pointer input.)
 */
export function createPhaserMusicFactory(scene: Phaser.Scene): MusicFactory {
  return (key: MusicKey): HowlLike =>
    scene.cache.audio.has(key) ? new PhaserHowl(scene, key) : new NoopHowl();
}
